import { Button, FlexBox } from '@dixa/lib-shared-habla';
import type {
  SearchFilterConditionCustomAttribute,
  SearchFilterConditionString,
  SearchFilterField,
  SearchFilters,
} from 'lib-search-types';
import {
  convertFiltersObjectToString,
  convertFiltersStringToObject,
  filterOperatorCatalogue,
  isLeafFilterCondition,
  SearchFilter,
  type SearchFilterChangeEvent,
} from 'lib-search-ui';
import { useCallback, useMemo } from 'react';

import {
  SearchCustomAttributesFilter,
  SearchScopeFilter,
} from 'components/search';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { userTypeAssets } from 'utils/user';

const userTypeItems = Object.entries(userTypeAssets)
  .filter(([id]) => id !== 'bot')
  .map(([id, { prettyName, icon }]) => ({
    value: id,
    label: prettyName,
    icon,
  }));

export function ContactsFilters(): JSX.Element {
  const { params, setParam, unsetParam } =
    useAppSearchParams('/contacts/user/:id');

  const filters = useMemo(
    () =>
      params.filters
        ? convertFiltersStringToObject<'users'>(params.filters).filters.filter(
            isLeafFilterCondition,
          )
        : [],
    [params.filters],
  );

  const userTypeFilter = filters.find(
    (condition) => condition.field === 'user_type',
  ) as SearchFilterConditionString<'user_type'> | undefined;

  const companyFilter = filters.find((c) => c.field === 'company_id') as
    | SearchFilterConditionString<'company_id', true>
    | undefined;

  const contactAttributeFilters = filters.filter(
    (c) => c.field === 'contact_custom_attribute',
  ) as SearchFilterConditionCustomAttribute<'contact_custom_attribute'>[];

  const persistFilters = useCallback(
    (nextFilters: typeof filters) => {
      const encodedFilters = convertFiltersObjectToString({
        strategy: 'all',
        filters: nextFilters,
      } satisfies SearchFilters<'users'>);

      encodedFilters
        ? setParam('filters', encodedFilters)
        : unsetParam('filters');
    },
    [setParam, unsetParam],
  );

  const handleFilterChange = useCallback(
    (
      field: SearchFilterField<'users'>,
      { action, operator, values }: SearchFilterChangeEvent,
    ) => {
      const rest = filters.filter((c) => c.field !== field);

      persistFilters(
        action === 'upsert'
          ? [...rest, { field, operator, values } as (typeof filters)[number]]
          : rest,
      );
    },
    [filters, persistFilters],
  );

  const handleCustomAttributesFilterChange = useCallback(
    (
      condition: SearchFilterConditionCustomAttribute<'contact_custom_attribute'> | null,
      attributeId: string,
    ) => {
      const rest = filters.filter(
        (c) =>
          c.field !== 'contact_custom_attribute' ||
          (c as { attributeId?: string }).attributeId !== attributeId,
      );

      persistFilters(
        condition ? [...rest, condition as (typeof filters)[number]] : rest,
      );
    },
    [filters, persistFilters],
  );

  const handleClearAll = useCallback(() => {
    unsetParam('filters');
  }, [unsetParam]);

  return (
    <FlexBox gap="200" alignItems="center" flexWrap="wrap">
      <SearchFilter
        items={userTypeItems}
        defaultOperator="is_one"
        selectedValues={userTypeFilter?.values}
        selectedOperator={userTypeFilter?.operator}
        onChange={(e) => handleFilterChange('user_type', e)}
        showSearch={false}
      >
        Category
      </SearchFilter>

      <SearchScopeFilter
        scope="companies"
        placeholder="Search companies..."
        operators={filterOperatorCatalogue.users.company_id.operators}
        defaultOperator={
          filterOperatorCatalogue.users.company_id.defaultOperator
        }
        selectedValues={companyFilter?.values}
        selectedOperator={companyFilter?.operator}
        initialArg={{ sortBy: { field: 'name', order: 'asc' } }}
        onChange={(e) => handleFilterChange('company_id', e)}
      >
        Company
      </SearchScopeFilter>

      <SearchCustomAttributesFilter
        entityId="contact"
        conditions={contactAttributeFilters}
        onChange={handleCustomAttributesFilterChange}
      >
        Attributes
      </SearchCustomAttributesFilter>

      {filters.length > 0 && (
        <Button variant="tertiary" onClick={handleClearAll}>
          Clear all
        </Button>
      )}
    </FlexBox>
  );
}
